import { useEffect, useState } from 'react';
import { files, type Attachment } from '../lib/api.ts';

interface Props {
  readonly messageId: string;
}

const sizeOf = (bytes: string): string => {
  const n = Number(bytes);
  return n < 1024 * 1024
    ? `${Math.round(n / 1024)} KB`
    : `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Thumbnails load through the same signed-URL path as full files, so a
 * strip of images costs one short-lived link each rather than exposing
 * storage keys. Links are fetched on mount for thumbs and on click for
 * originals -- a 60 second URL prefetched for every file on the page
 * would be dead before anyone clicked it.
 */
export const Attachments = ({ messageId }: Props) => {
  const [list, setList] = useState<Attachment[]>([]);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [viewing, setViewing] = useState<{ url: string; name: string } | null>(
    null,
  );

  useEffect(() => {
    let live = true;

    files
      .list(messageId)
      .then(async (rows) => {
        if (!live) return;
        setList(rows);

        const pairs = await Promise.all(
          rows
            .filter((r) => r.has_thumb)
            .map(async (r) => {
              const signed = await files.sign(r.id, 'thumb').catch(() => null);
              return signed ? ([r.id, signed.url] as const) : null;
            }),
        );

        if (!live) return;
        setThumbs(Object.fromEntries(pairs.filter((p) => p !== null)));
      })
      .catch(() => undefined);

    // Guards against setState after the message scrolls out and the
    // component unmounts mid-request.
    return () => {
      live = false;
    };
  }, [messageId]);

  const open = async (a: Attachment) => {
    const signed = await files.sign(a.id, 'full').catch(() => null);
    if (!signed) return;
    if (a.mime === 'application/pdf') {
      window.location.href = signed.url;
      return;
    }
    setViewing({ url: signed.url, name: a.filename });
  };

  if (list.length === 0) return null;

  return (
    <div className="attach">
      {list.map((a) => (
        <button
          key={a.id}
          className="attachItem"
          onClick={() => open(a)}
          title={`${a.filename} - ${sizeOf(a.bytes)}`}
        >
          {thumbs[a.id] ? (
            <img src={thumbs[a.id]} alt={a.filename} loading="lazy" />
          ) : (
            <span className="attachDoc">PDF</span>
          )}
          <span className="attachName">{a.filename}</span>
        </button>
      ))}

      {viewing && (
        <div
          className="lightbox"
          role="dialog"
          onClick={() => setViewing(null)}
        >
          <img src={viewing.url} alt={viewing.name} />
          <span className="lightboxName">{viewing.name}</span>
        </div>
      )}
    </div>
  );
};
